import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DialogAppBar from "../../../../Components/DialogAppBar";
import StockItemSearch from "../../../../Components/StockItems/StockItemSearch";

const defaultForm = {
  name: "",
  description: "",
  stockItem: null,
};

export default function FeatureItemDialog({
  open,
  onClose,
  onSubmit,
  onDelete,
  feature,
  isSubmitting,
  isDeleting,
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: feature?.name || "",
        description: feature?.description || "",
        stockItem: feature?.StockItem || null,
      });
      setErrors({});
    } else {
      setForm(defaultForm);
      setErrors({});
    }
  }, [open, feature]);

  const handleFieldChange = (field) => (event) => {
    setForm({
      ...form,
      [field]: event.target.value,
    });
  };

  const handleSubmit = () => {
    const trimmedName = form.name.trim();
    if (!trimmedName) {
      setErrors({
        name: t(
          "manufacturing.featureNameRequired",
          "Feature name is required."
        ),
      });
      return;
    }
    setErrors({});
    onSubmit({
      name: trimmedName,
      description: form.description ? form.description.trim() : "",
      stockItemId: form.stockItem?.id || null,
    });
  };

  const header = feature
    ? t("manufacturing.editFeature", "Edit feature")
    : t("manufacturing.addFeature", "Add feature");

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogAppBar header={header} onClose={onClose} />
      <DialogContent style={{ marginTop: 80, paddingBottom: 120 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label={t("manufacturing.featureName", "Feature name")}
              value={form.name}
              onChange={handleFieldChange("name")}
              variant="outlined"
              fullWidth
              required
              disabled={isSubmitting}
              error={Boolean(errors.name)}
              helperText={errors.name}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t("manufacturing.featureDescription", "Feature description")}
              value={form.description}
              onChange={handleFieldChange("description")}
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              disabled={isSubmitting}
            />
          </Grid>
          <Grid item xs={12}>
            {form.stockItem ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                border={1}
                borderRadius={8}
                borderColor="grey.300"
                p={2}
              >
                <Box>
                  <Typography variant="subtitle2">
                    {form.stockItem.sku}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {form.stockItem.name}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    setForm({
                      ...form,
                      stockItem: null,
                    })
                  }
                  disabled={isSubmitting}
                >
                  {t("common.change", "Change")}
                </Button>
              </Box>
            ) : (
              <StockItemSearch
                allowNewItems={false}
                clearOnSelect
                disableGridWrapper
                label={t("manufacturing.linkedStockItem", "Linked stock item")}
                placeholder={t(
                  "manufacturing.searchStockItems",
                  "Search stock items"
                )}
                onChange={(_, value) => {
                  if (value && value.id) {
                    setForm({
                      ...form,
                      stockItem: value,
                    });
                  }
                }}
              />
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <Box
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          padding: 16,
          backgroundColor: "white",
          boxShadow: "0px -4px 12px rgba(0, 0, 0, 0.1)",
          zIndex: 10,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Button
              color="primary"
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? t("common.saving", "Saving…") : header}
            </Button>
          </Grid>
          <Grid item>
            <Button onClick={onClose} variant="outlined" disabled={isSubmitting}>
              {t("common.cancel", "Cancel")}
            </Button>
          </Grid>
          {feature && onDelete ? (
            <Grid item>
              <Button
                color="secondary"
                variant="contained"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isSubmitting || isDeleting}
              >
                {isDeleting
                  ? t("common.deleting", "Deleting…")
                  : t("common.delete", "Delete")}
              </Button>
            </Grid>
          ) : null}
        </Grid>
      </Box>
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <DialogTitle>
          {t("manufacturing.deleteFeature", "Delete feature")}
        </DialogTitle>
        <DialogContent>
          {t(
            "manufacturing.confirmDeleteFeature",
            "Are you sure you want to delete this feature? This action cannot be undone."
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowDeleteConfirm(false)}
            variant="outlined"
            disabled={isDeleting}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            onClick={() => {
              setShowDeleteConfirm(false);
              onDelete();
            }}
            color="secondary"
            variant="contained"
            disabled={isDeleting}
          >
            {t("common.delete", "Delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
